/**
 * WorkspaceLibraryBar — единая библиотека workspace в Header (P1.4).
 *
 * Заменяет per-tab LibraryBar на ОДИН dropdown: одна library = composite
 * snapshot по всем 4 вкладкам (search/generation/matching/files_transform).
 *
 * Поведение:
 *  - `useLibraries(null, currentProjectId)` — все библиотеки проекта (+ personal),
 *    unified (kind=null) + legacy per-kind.
 *  - <select> всех библиотек; value = выбранный id из useWorkspaceLibraryStore.
 *  - On select: select(projectId, id) → apply.mutateAsync({libraryId}) →
 *    applyWorkspaceSnapshot(composite). Unified ответ уже schema_version 2;
 *    legacy flat ответ оборачиваем как { schema_version: 2, [kind]: snapshot }.
 *  - «Save» → SaveLibraryDialog (kind=null, composite via buildWorkspaceSnapshot).
 *  - «Reset» → resetWorkspaceState() + clear(projectId) + сброс dropdown.
 *    Без confirm-modal: Reset только чистит unsaved working state, recoverable
 *    пере-выбором библиотеки.
 *  - Rename (pencil) + Delete (trash) на выбранной библиотеке — тот же UX,
 *    что в LibraryBar (inline rename input, delete confirm <Modal>).
 *
 * i18n: libraries.* (включая новый libraries.reset).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookMarked,
  Save,
  Trash2,
  Pencil,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

import {
  useLibraries,
  useApplyLibrary,
  useDeleteLibrary,
  useUpdateLibrary,
  type LibraryKind,
} from '@/hooks/useLibraries';
import { useProjectStore } from '@/store/projectStore';
import { useWorkspaceLibraryStore } from '@/store/workspaceLibraryStore';
import {
  buildWorkspaceSnapshot,
  applyWorkspaceSnapshot,
  resetWorkspaceState,
} from '@/store/librarySnapshots';
import { Modal } from '@/components/ui/Modal';
import { SaveLibraryDialog } from './SaveLibraryDialog';

export function WorkspaceLibraryBar() {
  const { t } = useTranslation();
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const { data, isLoading } = useLibraries(null, currentProjectId);
  const apply = useApplyLibrary();
  const del = useDeleteLibrary();
  const upd = useUpdateLibrary();

  // Подписка на selectedByProject чтобы select/clear реагировали реактивно.
  const selectedByProject = useWorkspaceLibraryStore((s) => s.selectedByProject);
  const select = useWorkspaceLibraryStore((s) => s.select);
  const clear = useWorkspaceLibraryStore((s) => s.clear);
  const selectedId = currentProjectId
    ? selectedByProject[currentProjectId] ?? ''
    : '';

  const [saveOpen, setSaveOpen] = useState(false);
  const [appliedName, setAppliedName] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const items = data?.items ?? [];
  const pendingDeleteItem = items.find((i) => i.id === pendingDeleteId);
  const renamingItem = items.find((i) => i.id === renamingId);

  async function handleSelect(id: string) {
    if (!currentProjectId) return;
    if (!id) {
      // Пустой option — сброс выбора (без reset state).
      clear(currentProjectId);
      setWarnings(null);
      setAppliedName(null);
      return;
    }
    const lib = items.find((i) => i.id === id);
    if (!lib) return;
    try {
      const res = await apply.mutateAsync({ libraryId: id });
      // Нормализуем ответ в snapshot-запись.
      //  - unified / search / gen / matching legacy: { snapshot, warnings, kind }
      //  - files_transform legacy: FilesTransformApplyResponse (без snapshot)
      const isFilesTransformLegacy =
        !('snapshot' in res) &&
        ('selected_sheet' in res || 'column_renames' in res);
      const flatSnapshot: Record<string, unknown> = isFilesTransformLegacy
        ? {
            schema_version: 1,
            selected_sheet: (res as { selected_sheet?: string | null })
              .selected_sheet,
            column_renames: (res as { column_renames?: Record<string, string> })
              .column_renames,
            selected_columns: (res as { selected_columns?: string[] })
              .selected_columns,
          }
        : (res as { snapshot: Record<string, unknown> }).snapshot ?? {};
      const serverWarnings = (res as { warnings?: string[] }).warnings ?? [];

      // unified (kind=null) → composite уже schema_version 2.
      // legacy (kind!=null) → оборачиваем { schema_version: 2, [kind]: flat }.
      const libKind: LibraryKind | null = lib.kind;
      const composite: Record<string, unknown> =
        libKind === null
          ? flatSnapshot
          : { schema_version: 2, [libKind]: flatSnapshot };

      const localWarns = applyWorkspaceSnapshot(composite);
      const allWarns = [...serverWarnings, ...localWarns];

      select(currentProjectId, id);
      setAppliedName(lib.name);
      setWarnings(allWarns.length > 0 ? allWarns : null);
      setTimeout(() => setAppliedName(null), 3000);
    } catch (e) {
      setWarnings([String(e)]);
    }
  }

  function handleReset() {
    resetWorkspaceState();
    if (currentProjectId) clear(currentProjectId);
    setWarnings(null);
    setAppliedName(null);
  }

  async function confirmDelete() {
    if (!pendingDeleteId) return;
    await del.mutateAsync(pendingDeleteId);
    if (currentProjectId && selectedId === pendingDeleteId) {
      clear(currentProjectId);
    }
    setPendingDeleteId(null);
  }

  function startRename(id: string) {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    setRenameValue(item.name);
    setRenamingId(id);
  }
  async function commitRename() {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    try {
      await upd.mutateAsync({ libraryId: renamingId, body: { name: trimmed } });
    } finally {
      setRenamingId(null);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <BookMarked className="w-4 h-4 text-surface-400" />

      {renamingId && renamingItem ? (
        <input
          className="input max-w-[14rem]"
          autoFocus
          value={renameValue}
          maxLength={120}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') setRenamingId(null);
          }}
          onBlur={commitRename}
        />
      ) : (
        <select
          className="input max-w-[14rem]"
          value={selectedId}
          onChange={(e) => handleSelect(e.target.value)}
          disabled={isLoading || items.length === 0}
          aria-label={t('libraries.title', { defaultValue: 'Libraries' })}
        >
          <option value="">
            {items.length === 0
              ? t('libraries.empty', { defaultValue: 'No saved libraries' })
              : t('libraries.loadPlaceholder', { defaultValue: '— Library —' })}
          </option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </select>
      )}

      {/* Rename (pencil) */}
      <button
        type="button"
        className="btn-secondary px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50"
        onClick={() => selectedId && startRename(selectedId)}
        disabled={!selectedId || upd.isPending}
        title={t('libraries.rename', { defaultValue: 'Rename' })}
        aria-label={t('libraries.rename', { defaultValue: 'Rename' })}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>

      {/* Delete (trash) */}
      <button
        type="button"
        className="btn-secondary px-2 py-1 inline-flex items-center gap-1 text-danger disabled:opacity-50"
        onClick={() => selectedId && setPendingDeleteId(selectedId)}
        disabled={!selectedId || del.isPending}
        title={t('libraries.delete', { defaultValue: 'Delete' })}
        aria-label={t('libraries.delete', { defaultValue: 'Delete' })}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      {/* Save (composite) */}
      <button
        type="button"
        className="btn-primary inline-flex items-center gap-1.5 px-2 py-1"
        onClick={() => setSaveOpen(true)}
        title={t('libraries.save', { defaultValue: 'Save as library' })}
      >
        <Save className="w-3.5 h-3.5" />
        {t('libraries.save', { defaultValue: 'Save' })}
      </button>

      {/* Reset */}
      <button
        type="button"
        className="btn-secondary px-2 py-1 inline-flex items-center gap-1"
        onClick={handleReset}
        title={t('libraries.resetTitle', { defaultValue: 'Reset settings' })}
        aria-label={t('libraries.reset', { defaultValue: 'Reset' })}
      >
        <RotateCcw className="w-3.5 h-3.5" />
      </button>

      {/* Status */}
      {appliedName && (
        <span className="text-xs text-success inline-flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t('libraries.applySuccess', {
            defaultValue: 'Applied: {{name}}',
            name: appliedName,
          })}
        </span>
      )}
      {warnings && warnings.length > 0 && (
        <span
          className="text-xs text-warning inline-flex items-center gap-1"
          title={warnings.join('; ')}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          {t('libraries.warningsCount', {
            defaultValue: '{{count}} warning(s)',
            count: warnings.length,
          })}
        </span>
      )}

      {/* Delete confirm */}
      <Modal
        open={Boolean(pendingDeleteItem)}
        onClose={() => setPendingDeleteId(null)}
        title={t('libraries.confirmDeleteTitle', { defaultValue: 'Delete library?' })}
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setPendingDeleteId(null)}
              disabled={del.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              className="btn-primary bg-danger-strong hover:bg-danger-hover"
              onClick={confirmDelete}
              disabled={del.isPending}
            >
              {t('libraries.delete', { defaultValue: 'Delete' })}
            </button>
          </>
        }
      >
        <p className="text-sm text-surface-300">
          {pendingDeleteItem &&
            t('libraries.confirmDeleteBody', {
              defaultValue:
                'Are you sure you want to delete "{{name}}"? This cannot be undone.',
              name: pendingDeleteItem.name,
            })}
        </p>
      </Modal>

      <SaveLibraryDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        kind={null}
        onBuildSnapshot={buildWorkspaceSnapshot}
        projectId={currentProjectId}
      />
    </div>
  );
}